import { useId, useState, type ReactNode } from "react";
import type { CallerNumbersResponse, OrganizationRole, TeamInvitation, TeamMember, TeamRow, WhatsAppSenderPerson, WhatsAppSendersResponse } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { canActOn, canCancelInvitation } from "@gioia365/domain/access";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { rolesQuery, teamQuery, useCancelInvitation, useMembershipTransition, useTab, type MembershipTransition } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { Chip } from "../../design/components/Chip";
import { DataTable } from "../../design/components/DataTable";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { BillingRecoveryTeam } from "../../team/BillingRecoveryTeam";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { StatusPill } from "../../design/components/StatusPill";
import { useToast } from "../../design/components/Toast";
import type { Tone } from "../../design/components/tone";
import { messageForError } from "../../i18n/errors";
import { formatNumericDate } from "../../i18n/format";
import { useT } from "../../i18n/locale";
import { leadOwnerCountsQuery, useReassignLeads } from "../../leads/lead-queries";
import { assignableRoles, canGiveRole, leadScopeOf, useAccess, type Access } from "../../session/access";
import { callerNumbersQuery, numberOf } from "../../telephony/caller-number-queries";
import { whatsAppSendersQuery } from "../../whatsapp/settings-api";
import type { LeadsChoice, ReassignBlock } from "../../team/SuspendedLeadsBlock";
import { ManualLinkDialog } from "../manual-link-dialog";
import { CallerNumberCell, CallerNumberDialog, CallerNumbersSection, hasNumberControl, poolSummary } from "./caller-numbers";
import { ConfirmStatusDialog, InvitationDialog, InviteDialog, MemberDialog, teamErrorMessage } from "./team-dialogs";
import { SenderNumber, WhatsAppSenderDialog, WhatsAppTeamCell } from "./whatsapp-sender";
import "./team.css";

// Impostazioni → Team (piano M1b Task 7, mock-up `team.html`; la colonna «WhatsApp» con M6, qui sotto). La vede
// ogni persona attiva dell'azienda; cambiarla richiede `manage_team`. Le regole di livello e di poteri
// (`@gioia365/domain/access`) decidono soltanto cosa si mostra: il server controlla comunque ogni richiesta, e un suo
// rifiuto si spiega sempre.
//
// I Lead di chi smette di lavorare (piano M2 Task 22): chi gestisce il team legge quanti Lead ha ogni owner
// (`GET /api/lead-owners/counts`); la riga di chi è sospeso o archiviato dice quanti restano da riassegnare, la conferma
// di sospensione offre di riassegnarli a turno e quella di archiviazione li riassegna prima di archiviare. Nella Vista
// Commerciale i conteggi sono quelli del perimetro osservato e la riassegnazione non si offre: il server la rifiuterebbe
// sempre, perché misura «modificare tutti i Lead» sul Commerciale osservato (questione aperta 119). Se i conteggi non si
// leggono, la pagina lo dice sopra la tabella con «Riprova», e righe, «Gestisci» e conferme dicono che il conteggio manca:
// mai come se la persona non avesse Lead.
//
// Il parco dei cellulari (piano M4, Task 17, `caller-numbers.tsx`): la colonna «Numero per chiamare» dopo «Stato», il
// riepilogo sotto il titolo e la sezione «Parco numeri per chiamare» sotto la tabella ci sono soltanto quando il server lascia
// leggere il parco (`GET /api/caller-numbers`); a un `403`, o su un server senza la telefonia, semplicemente non ci sono, e se
// la lettura non riesce la sezione lo dice, con «Riprova». Il numero si dà da «Gestisci» e, sulle righe senza «Gestisci» (il
// titolare, la propria, chi non si gestisce), con «Assegna» o «Cambia» nella cella, a chi ha la sezione «Numeri per chiamare»
// (revisione finale del Task 17: ogni persona attiva può avere un cellulare).
//
// La colonna «WhatsApp» (piano M6, R1, Task 5a; mock-up `mockup.js`, `waCell`) dopo «Numero per chiamare»: il WhatsApp di ogni
// persona (`GET /api/whatsapp/senders`, che legge ogni persona attiva), in breve, con il numero e lo stato, e «Imposta» per chi il
// server lascia impostare (`canSet`), con la stessa finestra di «Invio dei WhatsApp» (`whatsapp-sender.tsx`). Gli inviti e gli
// archiviati non hanno un WhatsApp: il segno vuoto. Se l'elenco non si legge la pagina lo dice sopra la tabella, con «Riprova», e la
// colonna non c'è; un server senza le rotte dei WhatsApp (la demo locale, `404 not_found`) non ha la colonna, senza errore.

/** La finestra aperta, con la riga com'era all'apertura: una rilettura del team non la chiude sotto le mani. */
type Panel =
  | { kind: "invite"; from?: TeamMember }
  | { kind: "member"; member: TeamMember }
  | { kind: "invitation"; invitation: TeamInvitation }
  | { kind: "confirm"; action: "suspend" | "archive"; member: TeamMember }
  | { kind: "number"; member: TeamMember }
  | { kind: "whatsapp"; person: WhatsAppSenderPerson }
  | { kind: "link"; name: string; link: string };

export function TeamPage() {
  const access=useAccess();
  if(access.context.billingRecovery)return <SettingsLayout section="team"><BillingRecoveryTeam /></SettingsLayout>;
  return (
    <SettingsLayout section="team">
      <TeamSection />
    </SettingsLayout>
  );
}

const isArchived = (row: TeamRow) => row.kind === "membership" && row.status === "archived";

/** Un invito scaduto va mandato di nuovo: la tabella lo dice. */
function isPast(iso: string): boolean {
  return Date.parse(iso) <= Date.now();
}

function counts(rows: readonly TeamRow[]) {
  const people = rows.filter((row) => !isArchived(row));
  const members = people.filter((row): row is TeamMember => row.kind === "membership");
  return {
    total: people.length,
    active: members.filter((row) => row.status === "active").length,
    invited: people.length - members.length,
    // Chi sta uscendo (sospensione o archiviazione in corso) non entra già più: conta fra i sospesi.
    suspended: members.filter((row) => row.status === "suspended" || row.status === "suspending").length,
  };
}

function TeamSection() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const toast = useToast();
  const titleId = useId();
  const team = useQuery(teamQuery(tab));
  const roles = useQuery(rolesQuery(tab));
  const transition = useMembershipTransition(tab);
  const cancelInvitation = useCancelInvitation(tab);
  const reassign = useReassignLeads(tab);
  const [panel, setPanel] = useState<Panel | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const organization = access.context.organization.name;
  const canEdit = access.can("manage_team");
  // I conteggi li legge soltanto chi gestisce il team, come chiede il server; riassegnare chiede anche di cambiare
  // l'owner di tutti i Lead (questione aperta 77), mai nella Vista Commerciale: l'interfaccia lo rispecchia, il server
  // decide.
  const ownerCounts = useQuery({ ...leadOwnerCountsQuery(tab), enabled: canEdit });
  // Il parco lo legge chi gestisce il team o ha la sezione «Numeri per chiamare», e la piattaforma: per gli altri il server
  // risponderebbe sempre `403`, quindi non si chiede. `null` quando non c'è (rifiutato o non ancora letto).
  const canReadPool = access.isPlatform || canEdit || access.can("settings.phone_numbers");
  const poolQuery = useQuery({ ...callerNumbersQuery(tab), enabled: canReadPool });
  const pool = poolQuery.data ?? null;
  const poolFailed = poolQuery.isError ? { retry: () => void poolQuery.refetch(), retrying: poolQuery.isFetching } : undefined;
  const sendersQuery = useQuery(whatsAppSendersQuery(tab));
  const senders = sendersQuery.data ?? null;
  const sendersMissing = sendersQuery.error instanceof ApiRequestError && sendersQuery.error.status === 404 && sendersQuery.error.code === "not_found";
  const sendersFailed =
    sendersQuery.isError && !sendersMissing ? { retry: () => void sendersQuery.refetch(), retrying: sendersQuery.isFetching } : undefined;
  const view = tab.commercialView ?? null;
  let reassignBlock: ReassignBlock | null = null;
  if (view) reassignBlock = "view";
  else if (!(canEdit && access.can("change_owner") && leadScopeOf(access, "update") === "all")) reassignBlock = "power";
  // I conteggi non letti si dicono sopra la tabella, nella riga, in «Gestisci» e nella conferma, con «Riprova»: mai come se
  // la persona non avesse Lead. `null` è «conteggio non disponibile»; senza `manage_team` non si leggono e valgono zero.
  const countsFailed = canEdit && ownerCounts.isError ? { retry: () => void ownerCounts.refetch(), retrying: ownerCounts.isFetching } : undefined;
  const leadsOf = (membershipId: string): number | null => (countsFailed ? null : (ownerCounts.data?.get(membershipId) ?? 0));
  const rows = team.data;
  const allRoles = roles.data?.roles;
  const assignable = allRoles ? assignableRoles(access, allRoles) : [];
  const close = () => setPanel(null);

  // Le azioni di stato chiudono prima la finestra, poi l'avviso dice com'è andata (D16). Le promesse e non i
  // callback di `mutate`, che valgono soltanto per l'ultima chiamata: due azioni di fila hanno due avvisi.
  function changeStatus(member: TeamMember, action: MembershipTransition, choice: LeadsChoice = "keep") {
    setPanel(null);
    if (action === "archive" && choice === "reassign") {
      void reassignThenArchive(member);
      return;
    }
    const name = member.displayName;
    // Senza conteggi la conferma l'ha già detto e ha offerto soltanto «lasciali a lui»: l'avviso è quello di M1b.
    const leads = leadsOf(member.membershipId) ?? 0;
    const done: Record<MembershipTransition, string> = {
      suspend: t("team.done.suspended", { name }),
      resume: t("team.done.resumed", { name }),
      archive: t("team.done.archived", { name, organization }),
    };
    void transition.mutateAsync({ membershipId: member.membershipId, action }).then(
      async () => {
        if (action !== "suspend" || leads === 0) {
          toast.show(done[action], "success");
          return;
        }
        if (choice === "keep") {
          toast.show(leads === 1 ? t("team.done.suspendedKeptOne", { name }) : t("team.done.suspendedKept", { name, count: leads }), "success");
          return;
        }
        // Prima la sospensione, poi la riassegnazione: con `suspending` la persona non riceve già più Lead nuovi.
        try {
          const { reassigned } = await reassign.mutateAsync(member.membershipId);
          if (reassigned === 0) toast.show(done.suspend, "success");
          else toast.show(reassigned === 1 ? t("team.done.suspendedReassignedOne", { name }) : t("team.done.suspendedReassigned", { name, count: reassigned }), "success");
        } catch (error) {
          toast.show(t("team.done.suspendedNotReassigned", { name, reason: teamErrorMessage(t, error, { organization }) }), "danger");
        }
      },
      (error: unknown) => toast.show(teamErrorMessage(t, error, { organization }), "danger"),
    );
  }

  /** «Riassegna e archivia»: un Lead non resta mai a una persona archiviata, quindi prima i Lead, poi l'archiviazione. */
  async function reassignThenArchive(member: TeamMember) {
    const name = member.displayName;
    let reassigned: number;
    try {
      ({ reassigned } = await reassign.mutateAsync(member.membershipId));
    } catch (error) {
      toast.show(teamErrorMessage(t, error, { organization }), "danger");
      return;
    }
    try {
      await transition.mutateAsync({ membershipId: member.membershipId, action: "archive" });
    } catch (error) {
      toast.show(t("team.done.reassignedNotArchived", { name, reason: teamErrorMessage(t, error, { organization }) }), "danger");
      return;
    }
    if (reassigned === 0) toast.show(t("team.done.archived", { name, organization }), "success");
    else
      toast.show(
        reassigned === 1 ? t("team.done.archivedReassignedOne", { name, organization }) : t("team.done.archivedReassigned", { name, organization, count: reassigned }),
        "success",
      );
  }

  /** «Riassegnali agli altri User, a turno» da «Gestisci» di chi è già sospeso o archiviato. */
  function reassignLeft(member: TeamMember) {
    setPanel(null);
    const name = member.displayName;
    void reassign.mutateAsync(member.membershipId).then(
      ({ reassigned }) => toast.show(reassigned === 1 ? t("team.done.reassignedOne", { name }) : t("team.done.reassigned", { name, count: reassigned }), "success"),
      (error: unknown) => toast.show(teamErrorMessage(t, error, { organization }), "danger"),
    );
  }

  function cancel(invitation: TeamInvitation) {
    setPanel(null);
    const email = invitation.email;
    void cancelInvitation.mutateAsync(invitation.invitationId).then(
      () => toast.show(t("team.invitationDialog.cancelled", { email }), "success"),
      (error: unknown) => toast.show(teamErrorMessage(t, error, { organization, email }), "danger"),
    );
  }

  let content: ReactNode;
  if (team.isError || roles.isError) {
    content = (
      <div className="g-team__problem">
        <Reason tone="warning" role="alert">{messageForError(t, team.error ?? roles.error)}</Reason>
        <Button
          onClick={() => {
            void team.refetch();
            void roles.refetch();
          }}
        >
          {t("common.retry")}
        </Button>
      </div>
    );
  } else if (!rows || !allRoles) {
    content = <p className="g-team__counts">{t("common.loading")}</p>;
  } else {
    const archived = rows.filter(isArchived).length;
    const visible = showArchived ? rows : rows.filter((row) => !isArchived(row));
    content = (
      <>
        {countsFailed ? (
          <div className="g-team__problem">
            <Reason tone="warning" role="alert">
              {t("team.countsFailed")}
            </Reason>
            <Button onClick={countsFailed.retry} disabled={countsFailed.retrying} aria-busy={countsFailed.retrying ? "true" : undefined}>
              {t("common.retry")}
            </Button>
          </div>
        ) : null}
        {sendersFailed ? (
          <div className="g-team__problem">
            <Reason tone="warning" role="alert">
              {t("whatsappSendingSettings.team.failed")}
            </Reason>
            <Button onClick={sendersFailed.retry} disabled={sendersFailed.retrying} aria-busy={sendersFailed.retrying ? "true" : undefined}>
              {t("common.retry")}
            </Button>
          </div>
        ) : null}
        <DataTable caption={t("team.caption", { organization })} captionHidden>
          <thead>
            <tr>
              <th scope="col">{t("team.columns.person")}</th>
              <th scope="col">{t("team.columns.role")}</th>
              <th scope="col">{t("team.columns.status")}</th>
              {pool ? <th scope="col">{t("callerNumbers.column")}</th> : null}
              {senders ? <th scope="col">{t("whatsappSendingSettings.people.columns.channel")}</th> : null}
              <th scope="col">
                <span className="g-visually-hidden">{t("team.columns.action")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <TeamTableRow
                key={row.kind === "membership" ? row.membershipId : row.invitationId}
                row={row}
                access={access}
                roles={allRoles}
                canEdit={canEdit}
                leadsLeft={row.kind === "membership" ? leadsOf(row.membershipId) : 0}
                pool={pool}
                senders={senders}
                onManage={() => setPanel(row.kind === "membership" ? { kind: "member", member: row } : { kind: "invitation", invitation: row })}
                onNumber={() => {
                  if (row.kind === "membership") setPanel({ kind: "number", member: row });
                }}
                onWhatsApp={(person) => setPanel({ kind: "whatsapp", person })}
              />
            ))}
          </tbody>
        </DataTable>
        {archived > 0 ? (
          <p>
            <Button variant="ghost" onClick={() => setShowArchived((shown) => !shown)}>
              {showArchived ? t("team.hideArchived") : t("team.showArchived", { count: archived })}
            </Button>
          </p>
        ) : null}
      </>
    );
  }

  const summary = rows ? counts(rows) : null;

  return (
    <>
      <AboutBox title={t("team.about.title")}>
        <p>{t("team.about.text", { organization })}</p>
        <h2>{t("team.about.whoDecidesTitle")}</h2>
        <p>{t("team.about.whoDecides")}</p>
      </AboutBox>
      {canEdit ? null : <Reason>{t("team.readOnly")}</Reason>}
      {canEdit && view ? <Reason>{t("team.inView", { name: view.displayName })}</Reason> : null}
      <section className="g-team" aria-labelledby={titleId}>
        <div className="g-team__head">
          <div className="g-team__title">
            <h2 id={titleId}>{t("team.title", { organization })}</h2>
            {summary ? <p className="g-team__counts">{t("team.counts", summary)}</p> : null}
            {pool ? <p className="g-team__counts">{poolSummary(t, pool)}</p> : null}
          </div>
          {canEdit ? (
            <Button variant="primary" onClick={() => setPanel({ kind: "invite" })} disabled={!allRoles}>
              <Icon name="plus" />
              {t("team.invite")}
            </Button>
          ) : null}
        </div>
        {content}
      </section>
      {pool || poolFailed ? <CallerNumbersSection tab={tab} pool={pool} failed={poolFailed} /> : null}

      {panel?.kind === "invite" ? (
        <InviteDialog
          tab={tab}
          organization={organization}
          roles={assignable}
          {...(panel.from ? { from: panel.from } : {})}
          onClose={close}
          onManual={(name, link) => setPanel({ kind: "link", name, link })}
          onSent={(name, email) => {
            setPanel(null);
            toast.show(t("team.inviteDialog.sent", { email, name }), "success");
          }}
        />
      ) : null}
      {panel?.kind === "member" ? (
        <MemberDialog
          tab={tab}
          member={panel.member}
          organization={organization}
          roles={assignable}
          leads={leadsOf(panel.member.membershipId)}
          countsFailed={countsFailed}
          reassignBlock={reassignBlock}
          pool={pool}
          onClose={close}
          onReassign={() => reassignLeft(panel.member)}
          onInvite={() => setPanel({ kind: "invite", from: panel.member })}
          onSaved={(name) => {
            setPanel(null);
            toast.show(t("team.manageDialog.saved", { name }), "success");
          }}
          onStatus={(action) => {
            if (action === "resume") changeStatus(panel.member, action);
            else setPanel({ kind: "confirm", action, member: panel.member });
          }}
        />
      ) : null}
      {panel?.kind === "invitation" ? (
        <InvitationDialog
          tab={tab}
          invitation={panel.invitation}
          canResend={canResend(access, allRoles, panel.invitation)}
          organization={organization}
          onClose={close}
          // L'invito nuovo prende il posto soltanto della finestra dell'invito che l'ha chiesto, mai di un'altra.
          onResent={(renewed) => {
            const asked = panel.invitation.invitationId;
            setPanel((current) => (current?.kind === "invitation" && current.invitation.invitationId === asked ? { kind: "invitation", invitation: renewed } : current));
          }}
          onManual={(name, link) => setPanel({ kind: "link", name, link })}
          onCancel={() => cancel(panel.invitation)}
        />
      ) : null}
      {panel?.kind === "confirm" ? (
        <ConfirmStatusDialog
          action={panel.action}
          name={panel.member.displayName}
          organization={organization}
          // Mentre il server li conta la conferma aspetta; se non si possono contare, la finestra resta quella di M1b.
          leads={canEdit && ownerCounts.isPending ? null : leadsOf(panel.member.membershipId)}
          countsFailed={countsFailed}
          reassignBlock={reassignBlock}
          onCancel={close}
          onConfirm={(choice) => changeStatus(panel.member, panel.action, choice)}
        />
      ) : null}
      {panel?.kind === "number" && pool ? (
        <CallerNumberDialog
          tab={tab}
          member={panel.member}
          pool={pool}
          onClose={close}
          onSaved={(name) => {
            setPanel(null);
            toast.show(t("team.manageDialog.saved", { name }), "success");
          }}
        />
      ) : null}
      {panel?.kind === "whatsapp" ? (
        <WhatsAppSenderDialog
          tab={tab}
          person={panel.person}
          onClose={close}
          onSaved={(message) => {
            setPanel(null);
            toast.show(message, "success");
          }}
        />
      ) : null}
      {panel?.kind === "link" ? (
        <ManualLinkDialog
          open
          title={t("team.inviteDialog.readyTitle", { name: panel.name })}
          intro={t("team.inviteDialog.readyIntro", { name: panel.name })}
          link={panel.link}
          onClose={close}
        />
      ) : null}
    </>
  );
}

/**
 * Reinviare un invito è dare di nuovo il suo ruolo: livello e poteri (questione 38). Senza il ruolo nell'elenco,
 * no. L'invito del titolare porta il ruolo di sistema, che nessuno assegna: lo reinvia soltanto la piattaforma,
 * come fa il server (è lo stesso invito con un link nuovo, non un secondo Super admin).
 */
function canResend(access: Access, roles: readonly OrganizationRole[] | undefined, invitation: TeamInvitation): boolean {
  if (invitation.isProtectedOwner) return access.isPlatform;
  const role = roles?.find((candidate) => candidate.id === invitation.role.id);
  return role !== undefined && canGiveRole(access, role);
}

export type Look = { tone: Tone; icon: "check" | "clock" | "minus"; label: MessageKey };

/** Come si dice lo stato di una persona, con tono, icona e testo; lo usa anche «Ruoli e permessi». */
export function memberLook(member: TeamMember): Look {
  switch (member.status) {
    case "active":
      return { tone: "success", icon: "check", label: "team.status.active" };
    case "suspended":
      return { tone: "danger", icon: "minus", label: "team.status.suspended" };
    case "archived":
      return { tone: "neutral", icon: "minus", label: "team.status.archived" };
    case "suspending":
      // Il primo passo è fatto (non entra più); il resto lo completa il server.
      return { tone: "warning", icon: "clock", label: member.pendingTransition === "archived" ? "team.status.archiving" : "team.status.suspending" };
  }
}

type RowProps = {
  row: TeamRow;
  access: Access;
  roles: readonly OrganizationRole[];
  canEdit: boolean;
  /** I Lead non archiviati della persona: zero per un invito o senza `manage_team`, `null` se i conteggi non si sono letti. */
  leadsLeft: number | null;
  /** Il parco dei cellulari, per la colonna «Numero per chiamare»; `null` se non c'è (allora la colonna non c'è). */
  pool: CallerNumbersResponse | null;
  /** Il WhatsApp delle persone, per la colonna «WhatsApp»; `null` se non c'è (allora la colonna non c'è). */
  senders: WhatsAppSendersResponse | null;
  onManage: () => void;
  /** «Assegna» o «Cambia» nella cella del numero, sulle righe senza «Gestisci». */
  onNumber: () => void;
  /** «Imposta» nella cella del WhatsApp. */
  onWhatsApp: (person: WhatsAppSenderPerson) => void;
};

function TeamTableRow({ row, access, roles, canEdit, leadsLeft, pool, senders, onManage, onNumber, onWhatsApp }: RowProps) {
  const t = useT();
  const timeZone = access.context.settings.timeZone;
  const isSelf = row.kind === "membership" && row.membershipId === access.context.membershipId;

  let status: ReactNode;
  if (row.kind === "invitation") {
    status = (
      <>
        <StatusPill tone="warning" icon="clock">{t("team.status.invited")}</StatusPill>
        {isPast(row.expiresAt) ? (
          <span className="g-table__sub g-team__expired">
            <Icon name="alert" />
            {t("team.status.expiredOn", { date: formatNumericDate(timeZone, row.expiresAt) })}
          </span>
        ) : (
          <span className="g-table__sub">{t("team.status.invitedOn", { date: formatNumericDate(timeZone, row.createdAt) })}</span>
        )}
      </>
    );
  } else {
    const look = memberLook(row);
    status = (
      <>
        <StatusPill tone={look.tone} icon={look.icon}>{t(look.label)}</StatusPill>
        {/* Chi non lavora più e ha ancora dei Lead: restano segnalati da riassegnare (decisione del 26 settembre, n. 5). */}
        {row.status !== "active" && leadsLeft === null ? (
          <span className="g-table__sub g-team__leads-left">
            <Icon name="alert" />
            {t("team.leadsLeftUnknown")}
          </span>
        ) : null}
        {row.status !== "active" && leadsLeft !== null && leadsLeft > 0 ? (
          <span className="g-table__sub g-team__leads-left">
            <Icon name="alert" />
            {leadsLeft === 1 ? t("team.leadsLeftOne") : t("team.leadsLeft", { count: leadsLeft })}
          </span>
        ) : null}
      </>
    );
  }

  // Il titolare è protetto per tutti, anche per la piattaforma; per sé stessi c'è «Il tuo account» (`canActOn`
  // lo esclude). Un invito si gestisce con la sola regola di livello, come fa il server per annullarlo
  // (`canCancelInvitation`): i poteri (questione 38) limitano soltanto il reinvio, che dentro la finestra si spegne
  // con il motivo scritto. L'invito del titolare non si annulla: si gestisce soltanto se si può reinviare.
  let manageable = false;
  if (canEdit && row.kind === "membership") {
    manageable = canActOn(access.actor, { membershipId: row.membershipId, roleLevel: row.role.level, isProtectedOwner: row.isProtectedOwner }).ok;
  } else if (canEdit && row.kind === "invitation") {
    // Un ruolo che non è nell'elenco (archiviato) si giudica dal riassunto della riga: il livello basta qui, e
    // il ruolo di sistema non si archivia mai.
    const role = roles.find((candidate) => candidate.id === row.role.id);
    manageable = row.isProtectedOwner
      ? canResend(access, roles, row)
      : canCancelInvitation(access.actor, role ?? { level: row.role.level, isSystem: false });
  }
  let action: ReactNode = null;
  if (row.kind === "membership" && row.isProtectedOwner) action = <Chip icon="lock">{t("team.protected")}</Chip>;
  else if (manageable) {
    action = (
      <Button onClick={onManage} aria-label={t("team.manageNamed", { name: row.displayName })}>
        {t("team.manage")}
      </Button>
    );
  }

  // Il numero di chi non ha «Gestisci» su questa riga si dà dalla cella, a chi ha la sezione «Numeri per chiamare»: il server
  // lo permette a ogni persona attiva, titolare e sé stessi compresi.
  const number = pool && row.kind === "membership" ? numberOf(pool, row.membershipId) : null;
  let numberAction: ReactNode = null;
  if (row.kind === "membership" && hasNumberControl(pool, row, manageable)) {
    const name = row.displayName;
    numberAction = (
      <Button
        variant="ghost"
        onClick={onNumber}
        aria-label={number ? t("callerNumbers.numberDialog.changeNamed", { name }) : t("callerNumbers.numberDialog.assignNamed", { name })}
      >
        {number ? t("callerNumbers.numberDialog.change") : t("callerNumbers.numberDialog.assign")}
      </Button>
    );
  }

  // Il WhatsApp della persona: gli inviti e gli archiviati non sono nell'elenco.
  const whatsApp = senders && row.kind === "membership" ? (senders.people.find((person) => person.membershipId === row.membershipId) ?? null) : null;

  return (
    <tr>
      <th scope="row">
        <span className="g-team__who">
          <strong>{row.displayName}</strong>
          {row.isProtectedOwner ? <Chip>{t("team.owner")}</Chip> : null}
          {isSelf ? <Chip tone="accent">{t("team.you")}</Chip> : null}
          {/* Una persona nata sospesa dall'importazione da Close, con il nome che aveva lì (INGR-15). */}
          {row.kind === "membership" && row.fromClose === true ? <Chip>{t("team.fromClose")}</Chip> : null}
        </span>
        <span className="g-table__sub">{row.email}</span>
      </th>
      <td>{row.role.name}</td>
      <td>{status}</td>
      {pool ? (
        <td>
          <CallerNumberCell row={number} action={numberAction} />
        </td>
      ) : null}
      {senders ? <td>{whatsApp ? <WhatsAppTeamCell person={whatsApp} onSetup={() => onWhatsApp(whatsApp)} /> : <SenderNumber sender={null} />}</td> : null}
      <td>{action}</td>
    </tr>
  );
}
